/**
 * useShapeEditorKeys — keyboard shortcut handler for the Custom Shape Editor.
 *
 * Attaches a `keydown` (and matching `keyup` for Space) listener to the
 * document while mounted and tears it down on unmount. Each shortcut
 * dispatches the appropriate `shapeEditorSlice` action.
 *
 * Focus guard: shortcuts are suppressed when focus is inside a text input,
 * `<textarea>`, or an element with `contenteditable="true"` — preventing
 * conflicts with any inline text fields inside the modal.
 *
 * Space key: `keydown` stores the current active tool in a ref and activates
 * pan mode; `keyup` restores the previous tool.
 *
 * SF-SHAPED-008.
 */
import { useCallback, useEffect, useRef } from 'react';

import {
  clearSelectedSegments,
  redo,
  setActiveTool,
  type ShapeEditorTool,
  undo,
} from '@dfq/shared/stores/shapeEditor';

import { useAppDispatch, useAppSelector } from './useStore';

// ---------------------------------------------------------------------------
// Focus guard
// ---------------------------------------------------------------------------

/** Returns true when focus is in a text-entry element — shortcuts should not fire. */
function isFocusInTextField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  // Check both the DOM property and the attribute to handle jsdom quirks.
  if (
    (el as HTMLElement).isContentEditable ||
    el.getAttribute('contenteditable') === 'true' ||
    el.getAttribute('contenteditable') === ''
  ) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseShapeEditorKeysOptions {
  /**
   * Called when Delete or Backspace is pressed and at least one segment is
   * selected. The container wires this to the bridge's delete-node logic so
   * the hook stays free of Paper.js geometry.
   */
  onDeleteNodes?: () => void;
}

export function useShapeEditorKeys(options: UseShapeEditorKeysOptions = {}): void {
  const { onDeleteNodes } = options;
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((s) => s.shapeEditor.activeTool);

  // Stable ref so the keydown closure always calls the latest callback without
  // requiring the effect to re-register on every render.
  const onDeleteNodesRef = useRef(onDeleteNodes);
  useEffect(() => {
    onDeleteNodesRef.current = onDeleteNodes;
  }, [onDeleteNodes]);

  // Store the tool that was active before Space activates pan mode so we can
  // restore it reliably on keyup regardless of intervening state changes.
  const prePanToolRef = useRef<ShapeEditorTool | null>(null);
  // Track whether Space is currently held to avoid repeat keydown events
  // triggering repeated dispatches.
  const spaceHeldRef = useRef(false);

  // Keep a stable ref to activeTool for the keyup handler without requiring
  // the effect to re-register on every tool change.
  const activeToolRef = useRef<ShapeEditorTool>(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isFocusInTextField()) return;

      const { key, ctrlKey, shiftKey, repeat } = e;

      // ---- Ctrl+Z → undo ----
      if (ctrlKey && !shiftKey && key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(undo());
        return;
      }

      // ---- Ctrl+Shift+Z → redo ----
      if (ctrlKey && shiftKey && key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(redo());
        return;
      }

      // ---- Ctrl+Y → redo ----
      if (ctrlKey && key === 'y') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(redo());
        return;
      }

      // ---- Delete / Backspace → delete selected node(s) ----
      if (key === 'Delete' || key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        onDeleteNodesRef.current?.();
        return;
      }

      // ---- P → pen tool ----
      if (key === 'p' || key === 'P') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(setActiveTool('pen'));
        return;
      }

      // ---- V / A → select tool ----
      if (key === 'v' || key === 'V' || key === 'a' || key === 'A') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(setActiveTool('select'));
        return;
      }

      // ---- Escape → deselect all + cancel open pen path ----
      if (key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        dispatch(clearSelectedSegments());
        // If the user is in pen mode, return to select so any in-progress
        // open path is implicitly abandoned (the pen tool loses activation).
        if (activeToolRef.current === 'pen') {
          dispatch(setActiveTool('select'));
        }
        return;
      }

      // ---- Space (held) → temporary pan mode ----
      if (key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        if (repeat || spaceHeldRef.current) return;
        spaceHeldRef.current = true;
        prePanToolRef.current = activeToolRef.current;
        // Only activate pan if not already in pan mode (guard for rapid
        // key events before the ref is set).
        if (activeToolRef.current !== 'pan') {
          dispatch(setActiveTool('pan'));
        }
        return;
      }
    },
    [dispatch],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        spaceHeldRef.current = false;
        const prior = prePanToolRef.current;
        prePanToolRef.current = null;
        if (prior !== null) {
          dispatch(setActiveTool(prior));
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
}
